import { isUndefined } from 'lodash-es';
import type { RefObject } from 'react';

import type {
  DragEndProps,
  DragMoveProps,
  PointerEventModifiers,
  RelativeAbsolutePosition,
} from '../../../../types/pointer-events.js';
import type { InteractionTriggers } from '../../types.js';
import { centerPositionVertically } from '../../utils/center-position-vertically.js';

export const panDragStart = ({
  panDeltaRef,
  onPanStart,
}: {
  panDeltaRef: RefObject<number | undefined>;
  onPanStart?: (event: { triggeredBy: InteractionTriggers }) => void;
}) => {
  onPanStart?.({ triggeredBy: 'pointer' });
  panDeltaRef.current = 0;
};
export const panDragMove = ({
  event,
  height,
  panDeltaRef,
  onPanChange,
}: {
  event: DragMoveProps & PointerEventModifiers;
  height: number;
  panDeltaRef: RefObject<number | undefined>;
  onPanChange?: (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => void;
}) => {
  const {
    startHoldingAt: startHoldingAtProp,
    currentPosition: currentPositionProp,
  } = event;

  const startHoldingAt = centerPositionVertically(startHoldingAtProp, height);
  const currentPosition = centerPositionVertically(currentPositionProp, height);

  if (!isUndefined(panDeltaRef.current)) {
    const fullInteractionDelta =
      currentPosition.relative.x - startHoldingAt.relative.x;

    const currentDelta = fullInteractionDelta - panDeltaRef.current;

    panDeltaRef.current = fullInteractionDelta;

    onPanChange?.({ triggeredBy: 'pointer', delta: -currentDelta });
  }
};
export const panDragEnd = ({
  panDeltaRef,
  onPanEnd,
}: {
  panDeltaRef: RefObject<number | undefined>;
  onPanEnd?: (event: { triggeredBy: InteractionTriggers }) => void;
}) => {
  panDeltaRef.current = undefined;

  onPanEnd?.({ triggeredBy: 'pointer' });
};

export const panDragStartWithMiddleButton = ({
  panDeltaRef,
  onPanStart,
}: {
  panDeltaRef: RefObject<number | undefined>;
  onPanStart?: (event: { triggeredBy: InteractionTriggers }) => void;
}) => {
  onPanStart?.({ triggeredBy: 'pointer' });
  panDeltaRef.current = 0;
};

export const panDragMoveWithMiddleButton = ({
  event,
  panDeltaRef,
  onPanChange,
}: {
  event: DragMoveProps & PointerEventModifiers;
  panDeltaRef: RefObject<number | undefined>;
  onPanChange?: (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => void;
}) => {
  if (!isUndefined(panDeltaRef.current)) {
    const { currentPosition, startHoldingAt } = event;

    const fullInteractionDelta =
      currentPosition.relative.x - startHoldingAt.relative.x;

    const currentDelta = fullInteractionDelta - panDeltaRef.current;

    panDeltaRef.current = fullInteractionDelta;

    onPanChange?.({ triggeredBy: 'pointer', delta: -currentDelta });
  }
};

export const panDragEndWithMiddleButton = ({
  event,
  panDeltaRef,
  lastMousePositionRef,
  onPanEnd,
}: {
  event: DragEndProps & PointerEventModifiers;
  panDeltaRef: RefObject<number | undefined>;
  lastMousePositionRef: RefObject<RelativeAbsolutePosition | undefined>;
  onPanEnd?: (event: { triggeredBy: InteractionTriggers }) => void;
}) => {
  panDeltaRef.current = undefined;
  lastMousePositionRef.current = event.releasedAt;

  onPanEnd?.({ triggeredBy: 'pointer' });
};
